import React, { useCallback, useEffect } from 'react';
import { 
  Box, Typography, Card, CardContent, Button, 
  Paper, useTheme, IconButton, Divider
} from '@mui/material';
import { 
  CloudUpload as UploadIcon,
  ArrowForward as ArrowForwardIcon,
  Delete as DeleteIcon,
  ContentPaste as PasteIcon
} from '@mui/icons-material';

const AIImageUpload = ({ previewUrl, onFileSelect, onDrop, hasImage, onNext, onClear }) => {
  const theme = useTheme();

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onDrop(e);
  }, [onDrop]);
  
  const handlePaste = useCallback((e) => {
    if (e.clipboardData && e.clipboardData.items) {
      const items = e.clipboardData.items;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const blob = items[i].getAsFile();
          const file = new File([blob], 'pasted-image.png', { type: blob.type });
          onFileSelect({ target: { files: [file] } });
          break;
        }
      }
    }
  }, [onFileSelect]);

  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [handlePaste]);

  return (
    <Card elevation={3}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <UploadIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: 'primary.main', mb: 1 }} />
          <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Upload Chart Screenshot
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload a screenshot of your trading chart for AI analysis
          </Typography>
        </Box>

        <Box
          sx={{
            border: `2px dashed ${theme.palette.divider}`,
            borderRadius: 2,
            p: { xs: 2, sm: 4 },
            mb: 3,
            textAlign: 'center',
            backgroundColor: theme.palette.background.default,
            position: 'relative',
            minHeight: 200,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {previewUrl ? (
            <Box sx={{ position: 'relative', width: '100%' }}>
              <img
                src={previewUrl}
                alt="Chart Preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '300px',
                  borderRadius: theme.shape.borderRadius,
                  boxShadow: theme.shadows[2]
                }}
              />
              <IconButton
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.7)'
                  }
                }}
                onClick={() => {
                  // Reset the file input
                  const fileInput = document.getElementById('chart-upload');
                  if (fileInput) fileInput.value = '';
                  // Clear the preview
                  if (onClear) {
                    onClear();
                  } else {
                    onFileSelect({ target: { files: [] } });
                  }
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ) : (
            <>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="chart-upload"
                type="file"
                onChange={onFileSelect}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <label htmlFor="chart-upload">
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<UploadIcon />}
                    sx={{ mb: 1 }}
                  >
                    Select File
                  </Button>
                </label>
                
                <Button
                  variant="outlined"
                  onClick={() => document.execCommand('paste')}
                  startIcon={<PasteIcon />}
                >
                  Paste Screenshot
                </Button>
                
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  or drag and drop your image here
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  You can also press Ctrl+V to paste directly
                </Typography>
              </Box>
            </>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            disabled={!hasImage}
            onClick={onNext}
            endIcon={<ArrowForwardIcon />}
            sx={{ 
              minWidth: { xs: '100%', sm: 200 },
              py: { xs: 1, sm: 1.5 }
            }}
          >
            Continue to Analysis
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AIImageUpload;